import { z } from "zod";

import {
  BackgroundTasksLevelStreamEventSchema,
  SessionStateChangedStreamEventSchema,
  TaskSnapshotStreamEventSchema
} from "./stream-background-task-events.types";
import {
  GenerationStartEventSchema,
  RedactedThinkingStreamEventSchema,
  StreamStartEventSchema,
  TextDeltaEventSchema,
  TextEndEventSchema,
  TextStartEventSchema,
  ThinkingDeltaEventSchema,
  ThinkingEndEventSchema,
  ThinkingStartEventSchema,
  ToolInputAvailableEventSchema,
  ToolInputDeltaEventSchema,
  ToolInputStartEventSchema,
  ToolOutputAvailableEventSchema,
  UnknownBlockStreamEventSchema
} from "./stream-block-events.types";
import { ErrorEventSchema } from "./stream-error.types";
import {
  PeerMessageStreamEventSchema,
  SessionConfigStreamEventSchema,
  ThinkingTokensStreamEventSchema,
  UserMessageReplayStreamEventSchema
} from "./stream-session-config-events.types";
import {
  AuthStatusStreamEventSchema,
  ControlRequestProgressStreamEventSchema,
  ConversationResetStreamEventSchema,
  LoopBannerStreamEventSchema,
  LoopNotificationStreamEventSchema,
  PermissionDeniedStreamEventSchema,
  RateLimitStreamEventSchema,
  SessionPhaseStreamEventSchema,
  ToolProgressStreamEventSchema,
  ToolUseSummaryStreamEventSchema,
  WorkerShuttingDownStreamEventSchema
} from "./stream-session-events.types";
import {
  CommandsChangedStreamEventSchema,
  ContextCompactedStreamEventSchema,
  FilesPersistedStreamEventSchema,
  GenericSignalStreamEventSchema,
  MemoryRecallStreamEventSchema,
  ModelRefusalStreamEventSchema,
  PromptSuggestionStreamEventSchema
} from "./stream-signal-events.types";
import { MessageEndEventSchema, StepEndEventSchema } from "./stream-step-events.types";

export type {
  ChatStreamBackgroundTaskStatus,
  ChatStreamBackgroundTaskUsage,
  TerminalBackgroundTaskStatus
} from "./stream-background-task-events.types";
export {
  BACKGROUND_TASK_KIND_AGENT,
  ChatStreamBackgroundTaskStatusSchema,
  ChatStreamBackgroundTaskUsageSchema,
  isTerminalBackgroundTaskStatus,
  TERMINAL_BACKGROUND_TASK_STATUSES
} from "./stream-background-task-events.types";
export type { StreamErrorAction, StreamErrorCategory, StreamErrorCode } from "./stream-error.types";
export {
  STREAM_ERROR_CODES,
  StreamErrorActionSchema,
  StreamErrorCategorySchema
} from "./stream-error.types";
export {
  LEGACY_PEER_MESSAGE_STREAM_TYPE,
  PEER_MESSAGE_STREAM_TYPE,
  SESSION_CONFIG_STREAM_TYPE,
  THINKING_TOKENS_STREAM_TYPE
} from "./stream-session-config-events.types";
export {
  AUTH_STATUS_STREAM_TYPE,
  CONTROL_REQUEST_PROGRESS_STREAM_TYPE,
  LOOP_BANNER_STREAM_TYPE,
  LOOP_NOTIFICATION_STREAM_TYPE,
  PERMISSION_DENIED_STREAM_TYPE,
  RATE_LIMIT_STREAM_TYPE,
  SESSION_PHASE_STREAM_TYPE,
  TOOL_PROGRESS_STREAM_TYPE,
  TOOL_USE_SUMMARY_STREAM_TYPE,
  WORKER_SHUTTING_DOWN_STREAM_TYPE
} from "./stream-session-events.types";
export {
  COMMANDS_CHANGED_STREAM_TYPE,
  CONTEXT_COMPACTED_STREAM_TYPE,
  FILES_PERSISTED_STREAM_TYPE,
  GENERIC_SIGNAL_STREAM_TYPE,
  MEMORY_RECALL_STREAM_TYPE,
  MODEL_REFUSAL_STREAM_TYPE,
  PROMPT_SUGGESTION_STREAM_TYPE
} from "./stream-signal-events.types";
export type {
  ChargedPopulation,
  ChatStreamTokenUsage,
  FinishReason,
  ToolResultPayload,
  ToolVerdict
} from "./stream-usage.types";
export {
  ChargedPopulationSchema,
  ChatStreamTokenUsageSchema,
  FinishReasonSchema,
  ToolResultPayloadSchema,
  ToolVerdictSchema
} from "./stream-usage.types";

/**
 * Discriminated union of all in-stream SSE event variants. Every SSE message
 * MUST match one variant. The discriminant is `type`.
 *
 * This union carries NO abort variant, deliberately: a client detects its own
 * abort via `AbortController.signal.aborted`, and the server surfaces it to
 * tracing via `isAborted` on `completeGeneration` rather than to the wire.
 *
 * 🚨 That is OUR choice, and it is NOT the Vercel AI SDK's. `ai@7`'s
 * `packages/ai/src/ui-message-stream/ui-message-chunks.ts` declares an `abort`
 * chunk at :206 — along with `tool-output-error` (:110), `tool-input-error`
 * (:73) and `message-metadata` (:210), none of which the SDK's docs page lists.
 * So anything encoding this union onto the AI SDK UI Message Stream must emit
 * `abort` from the STOP verb, and must not infer from this file that the member
 * does not exist. Read `ui-message-chunks.ts`, not the docs page.
 *
 * @see apps/frontend/src/common/chat/conversation-stream/conversation-stream-machine.ts
 */
/**
 * Atomic per-file content event emitted after sandbox-generated files are persisted to S3.
 * One event per file, carrying client-usable URLs rather than raw sandbox paths.
 *
 * Must be emitted BEFORE `message-end` so the frontend SSE read loop can act
 * on it (the loop closes on `message-end`).
 *
 * Owner-conditional URL fields (exactly one will be set):
 * - CUE_CONVERSATION: `url` carries a 1-hour presigned inline URL for <img src> rendering.
 * - CHAT: `publicUrl` carries the file's URL for <img src> rendering. The object is private,
 *   so a renderable value is one the server signed for the reader.
 *
 * `downloadUrl` is the backend download endpoint (used for chip rendering).
 */
const FileEventSchema = z
  .object({
    type: z.literal("file"),
    fileId: z.string(),
    name: z.string(),
    mediaType: z.string(),
    /** CUE_CONVERSATION: 1-hour presigned inline URL for rendering. */
    url: z.string().url().optional(),
    /** CHAT: permanent direct S3 URL for rendering. */
    publicUrl: z.string().url().optional(),
    downloadUrl: z.string().url()
  })
  .refine((d) => !!d.url !== !!d.publicUrl, {
    message: "exactly one of url/publicUrl must be set"
  });

/**
 * Every shape the host may send the browser during a generation.
 *
 * **These members are plain objects rather than `.strict()` ones, so this union
 * STRIPS an undeclared key rather than refusing the frame.** The runner hop is
 * the opposite, and the asymmetry is the one that bites: a fact named there and
 * forgotten here does not fail, it vanishes, in the direction that reports nothing.
 * Declaring both halves in one change is what prevents it; no schema here will.
 * `apps/backend/src/__governance__/translator-writes-are-declared-at-the-browser-hop.spec.ts`
 * refuses a translator the registry dispatches that writes a key this union
 * does not declare.
 */
export const ChatStreamEventSchema = z.discriminatedUnion("type", [
  GenerationStartEventSchema,
  StreamStartEventSchema,
  TextStartEventSchema,
  TextDeltaEventSchema,
  TextEndEventSchema,
  ThinkingStartEventSchema,
  ThinkingDeltaEventSchema,
  ThinkingEndEventSchema,
  ToolInputStartEventSchema,
  ToolInputDeltaEventSchema,
  ToolInputAvailableEventSchema,
  ToolOutputAvailableEventSchema,
  UnknownBlockStreamEventSchema,
  RedactedThinkingStreamEventSchema,
  TaskSnapshotStreamEventSchema,
  BackgroundTasksLevelStreamEventSchema,
  SessionStateChangedStreamEventSchema,
  ConversationResetStreamEventSchema,
  SessionPhaseStreamEventSchema,
  ToolProgressStreamEventSchema,
  ToolUseSummaryStreamEventSchema,
  SessionConfigStreamEventSchema,
  ThinkingTokensStreamEventSchema,
  LoopNotificationStreamEventSchema,
  LoopBannerStreamEventSchema,
  PermissionDeniedStreamEventSchema,
  WorkerShuttingDownStreamEventSchema,
  ControlRequestProgressStreamEventSchema,
  AuthStatusStreamEventSchema,
  RateLimitStreamEventSchema,
  PeerMessageStreamEventSchema,
  GenericSignalStreamEventSchema,
  ContextCompactedStreamEventSchema,
  CommandsChangedStreamEventSchema,
  ModelRefusalStreamEventSchema,
  MemoryRecallStreamEventSchema,
  FilesPersistedStreamEventSchema,
  PromptSuggestionStreamEventSchema,
  UserMessageReplayStreamEventSchema,
  StepEndEventSchema,
  FileEventSchema,
  MessageEndEventSchema,
  ErrorEventSchema
]);

export type ChatStreamEvent = z.infer<typeof ChatStreamEventSchema>;

export type GenerationStartEvent = z.infer<typeof GenerationStartEventSchema>;
export type StreamStartEvent = z.infer<typeof StreamStartEventSchema>;
export type TextStartEvent = z.infer<typeof TextStartEventSchema>;
export type TextDeltaEvent = z.infer<typeof TextDeltaEventSchema>;
export type TextEndEvent = z.infer<typeof TextEndEventSchema>;
export type ThinkingStartEvent = z.infer<typeof ThinkingStartEventSchema>;
export type ThinkingDeltaEvent = z.infer<typeof ThinkingDeltaEventSchema>;
export type ThinkingEndEvent = z.infer<typeof ThinkingEndEventSchema>;
export type ToolInputStartEvent = z.infer<typeof ToolInputStartEventSchema>;
export type ToolInputDeltaEvent = z.infer<typeof ToolInputDeltaEventSchema>;
export type ToolInputAvailableEvent = z.infer<typeof ToolInputAvailableEventSchema>;
export type ToolOutputAvailableEvent = z.infer<typeof ToolOutputAvailableEventSchema>;
export type UnknownBlockStreamEvent = z.infer<typeof UnknownBlockStreamEventSchema>;
export type RedactedThinkingStreamEvent = z.infer<typeof RedactedThinkingStreamEventSchema>;
export type TaskSnapshotStreamEvent = z.infer<typeof TaskSnapshotStreamEventSchema>;
export type BackgroundTasksLevelStreamEvent = z.infer<typeof BackgroundTasksLevelStreamEventSchema>;
export type SessionStateChangedStreamEvent = z.infer<typeof SessionStateChangedStreamEventSchema>;
export type SessionConfigStreamEvent = z.infer<typeof SessionConfigStreamEventSchema>;
export type AuthStatusStreamEvent = z.infer<typeof AuthStatusStreamEventSchema>;
export type RateLimitStreamEvent = z.infer<typeof RateLimitStreamEventSchema>;
export type PeerMessageStreamEvent = z.infer<typeof PeerMessageStreamEventSchema>;
export type GenericSignalStreamEvent = z.infer<typeof GenericSignalStreamEventSchema>;
export type ContextCompactedStreamEvent = z.infer<typeof ContextCompactedStreamEventSchema>;
export type CommandsChangedStreamEvent = z.infer<typeof CommandsChangedStreamEventSchema>;
export type ModelRefusalStreamEvent = z.infer<typeof ModelRefusalStreamEventSchema>;
export type MemoryRecallStreamEvent = z.infer<typeof MemoryRecallStreamEventSchema>;
export type FilesPersistedStreamEvent = z.infer<typeof FilesPersistedStreamEventSchema>;
export type PromptSuggestionStreamEvent = z.infer<typeof PromptSuggestionStreamEventSchema>;
export type UserMessageReplayStreamEvent = z.infer<typeof UserMessageReplayStreamEventSchema>;
export type StepEndEvent = z.infer<typeof StepEndEventSchema>;
export type FileEvent = z.infer<typeof FileEventSchema>;
export type MessageEndEvent = z.infer<typeof MessageEndEventSchema>;
export type StreamErrorEvent = z.infer<typeof ErrorEventSchema>;

/**
 * Describes the lifecycle state of a streaming connection.
 * - `idle`       — No stream active
 * - `connecting` — Fetch started, waiting for first byte
 * - `streaming`  — Receiving stream events
 * - `error`      — Stream errored
 * - `done`       — Stream completed normally
 */
export type ConnectionStatus = "idle" | "connecting" | "streaming" | "error" | "done";

/**
 * Per-turn correlation metadata captured by tracing middleware.
 * One entry per provider turn (Anthropic/OpenAI = 1 turn, Daytona = N turns).
 */
export type TurnMetadata = Readonly<{
  stepIndex: number;
  requestId?: string; // Anthropic req_… / OpenAI resp_… (TODO: x-request-id parity)
  messageId?: string; // Anthropic BetaMessage.id (msg_…) — Daytona only
  model?: string; // resolved model name for this turn
  parentToolUseId?: string; // present on Daytona sub-agent turns only
}>;
